import { Button, MagellanPopup } from 'components/atoms';
import AccountSearchCard from 'components/molecules/card/account-swich-search/AccountSwichSearchCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface IAccount {
  id: string;
  labelName?: string | null;
}

interface IAccountSelectPopupFormProps {
  trigger: JSX.Element | ((isOpen: boolean) => JSX.Element) | undefined;
  accounts?: IAccount[];
  currentAccountId?: string;
  isLoading?: boolean;
  onSave?: (accountId: string, actions: PopupActions) => void;
}

const AccountSelectPopupForm = ({
  trigger,
  accounts,
  currentAccountId,
  isLoading,
  onSave,
}: IAccountSelectPopupFormProps) => {
  const popupRef = useRef<PopupActions | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setSelectedAccountId(currentAccountId ?? null);
    }
  }, [open, currentAccountId]);

  const mappedAccounts = useMemo(() => {
    return (accounts ?? []).map((acc) => ({
      ...acc,
      label: acc.labelName ?? `${t('setting.user.account')} ${acc.id}`,
    }));
  }, [accounts, t]);

  return (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(e) => {
        e?.stopPropagation();
        setOpen(false);
        setSelectedAccountId(null);
      }}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        <MagellanPopup.Header onClose={() => popupRef.current?.close()}>
          {t('setting.user.accountSwitch')}
        </MagellanPopup.Header>
        <div className="flex flex-col w-full gap-4 max-w-96 overflow-auto max-h-[450px] [scrollbar-width:none] ">
          {mappedAccounts
            .filter((acc) => acc.id !== currentAccountId)
            .map((acc) => {
              const isSelected = acc.id === selectedAccountId;

              return (
                <AccountSearchCard
                  key={acc.id}
                  account={acc}
                  isSelected={isSelected}
                  onClick={() => setSelectedAccountId(acc.id)}
                />
              );
            })}
        </div>

        <div className="flex justify-between w-full">
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>

          <Button
            variant={BUTTON_VARIANT.submit}
            disabled={!selectedAccountId || selectedAccountId === currentAccountId || isLoading}
            onClick={() => {
              if (!selectedAccountId || !popupRef.current || !onSave) return;
              onSave(selectedAccountId, popupRef.current);
            }}
          >
            <Button.Text text={t('button.save')} />
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default AccountSelectPopupForm;
